import { DocumentSectionItem } from '../types/formSchema';
import { ScaleValue, SCALE_CONFIG} from '../constants/scaleConfig';

/**
 * Helper function to create a document section item with consistent key and id
 * Eliminates duplication and potential sync issues between key/id properties
 */
export function createDocumentSection(
  id: string,
  name: string,
  options: Partial<Omit<DocumentSectionItem, 'id' | 'key' | 'name'>> = {}
): DocumentSectionItem {
  return {
    id,
    key: id, // Automatically sync key with id
    name,
    hasFilter: false,
    includeTable: false,
    includeCommentary: false,
    scale: SCALE_CONFIG.DEFAULT,
    ...options
  };
}

/**
 * Helper function to create a document section with filters
 */
export function createDocumentSectionWithFilters(
  id: string,
  name: string,
  filterOptions: {
    filterType?: 'dropdown' | 'static' | 'multiselect';
    filterDependsOn?: string;
    filterPlaceholder?: string;
    filterOptions?: Array<{ value: string; label: string }>;
  },
  options: Partial<Omit<DocumentSectionItem, 'id' | 'key' | 'name'>> = {}
): DocumentSectionItem {
  return {
    id,
    key: id,
    name,
    hasFilter: true,
    ...filterOptions,
    ...options
  };
}

/**
 * Helper function to create a parent section with children
 */
export function createParentSection(
  id: string,
  name: string,
  children: DocumentSectionItem[]
): DocumentSectionItem {
  return createDocumentSection(id, name, { children, isParent: true });
}

/**
 * Helper function to create a parent section with filters
 */
export function createParentSectionWithFilters(
  id: string,
  name: string,
  filterOptions: {
    filterType?: 'dropdown' | 'static' | 'multiselect';
    filterDependsOn?: string;
    filterPlaceholder?: string;
    filterOptions?: Array<{ value: string; label: string }>;
  },
  children: DocumentSectionItem[] = []
): DocumentSectionItem {
  return createDocumentSectionWithFilters(id, name, filterOptions, { children, isParent: true });
}

/**
 * Helper function to create a leaf section with specific configuration
 */
export function createLeafSection(
  id: string,
  name: string,
  options: {
    hasFilter?: boolean;
    filterType?: 'dropdown' | 'multiselect' | 'static';
    filterOptions?: Array<{ value: string; label: string }>;
    filterDependsOn?: string;
    filterPlaceholder?: string;
    includeTable?: boolean;
    includeCommentary?: boolean;
    scale?: ScaleValue;
    maxCount?: number;
  } = {}
): DocumentSectionItem {
  return createDocumentSection(id, name, { ...options, isParent: false });
}
